import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import getPassword from './getPassword';
import configureSettings from './configureSettings';

import * as SalesStore from 'pos/modules/main/SalesStore';
import { CertificateExpirationNotification, CustomerDisplay, LoginService, TakeOutOrdersMonitor } from 'pos/services';
import { AvailableUserDTO, GetDeviceConfigurationResponse, LicenseContainerDTO, Res, ResellerBannerDTO } from 'core/api';
import { LocalStorage, Result, SessionProvider } from 'core/infrastructure';
import { ThemeManager, PosScreen, Header, Sidebar, UserButton, LargeDeviceOnly, Footer, ActionButton, Filler, AcceptActionButton, VerifactuNotificationBar, PhoneOnly, Content } from 'pos/ui';

import LastSaleBanner from 'pos/modules/main/login/LastSaleBanner';
import { CashdrawerFacade, LoginFacade, RestaurantFacade } from 'pos/facades';
import { Command, Electron } from 'pos/common';
import { isEqual, startOfDay } from 'date-fns';
import { DateUtils } from 'core/utils';
import { ResellerBanner } from './ResellerBanner';
import getResellerPassword from './getResellerPassword';
import { Message, Modal, useOverlayExecutor } from 'core/ui-next/modal';
import { Toaster, useEffectOnce } from 'core/ui-next';
import { Box, VStack, Text, Grid, Span, Stack } from '@chakra-ui/react';
import { SidebarButton } from 'pos/ui/SidebarButton';
import { useBarcodeReaderRef } from 'core/infrastructure-next';
import { FingerprintReaderStatus } from 'pos/ui/FingerprintReaderStatus';
import { useFingerprintReaderRef } from 'pos/services/fingerprint';
import { ReaderStatus } from 'pos/common/BridgeProxy.Endpoints';
import { z } from 'zod';
import { FormRef, useControlsFor } from 'core/ui-next/controls';

let mustWarnUser = true;
const LOGIN_MODE_KEY = 'loginMode';

async function warnIfBusinessDayIsInThePast(businessDay: Date, today: Date) {

	if (mustWarnUser && !isEqual(businessDay, today)) {
		await Message.show(Res.Msg.MSG_WARN_ABOUT_DIFFERENT_DAY_AND_CURRENT_BUSINESS_DAY(businessDay, today));
		mustWarnUser = false;
	}
}

async function openDayIfNeeded(): Promise<Result> {
	const today = startOfDay(new Date());
	const businessDay = await CashdrawerFacade.getCurrentBusinessDay();

	if (businessDay !== null) {
		await warnIfBusinessDayIsInThePast(businessDay, today);
		return Result.ok();
	}

	const { canOpen, reason } = await CashdrawerFacade.canOpenDay();
	if (!canOpen) {
		await Message.show(reason);
		return Result.error();
	}

	return Command.execute('openDay');
}

function LicenseInfo(container: LicenseContainerDTO): React.ReactNode {

	const ownerInfo = container.OwnerFiscalName
		? <Box>
			<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: 'neutral.800', _dark: 'white' }} truncate>{container.OwnerFiscalName}</Text>
			<Text fontSize={'xs'} fontWeight={'normal'} color={{ base: 'neutral.800', _dark: 'white' }} truncate>{container.OwnerCif}</Text>
			<Text fontSize={'xs'} fontWeight={'normal'} color={{ base: 'neutral.500', _dark: 'neutral.400' }} truncate>{AGORA_VERSION}</Text>
		</Box>
		: <Box fontWeight={'semibold'} fontSize={'sm'} color={{ base: 'neutral.800', _dark: 'white' }}>{Res.Msg.MSG_UNREGISTERED()}</Box>;

	const isNearExpiration = container.TemporaryBundleExpirationDate && DateUtils.getDifferenceInDays(container.TemporaryBundleExpirationDate, new Date()) <= 30;

	const expirationInfo = container.TemporaryBundleExpirationDate
		? <Span fontWeight={'xs'} fontSize={'normal'} color={isNearExpiration ? 'error' : ''}>{Res.Msg.MSG_LICENSE_EXPIRATION_DATE(UI.Format.asDate(container.TemporaryBundleExpirationDate))}</Span>
		: null;

	return <Box width={{ smDown: 'full', sm: '65' }} textAlign={'center'} fontSize={{ smDown: 'xs', sm: 'sm' }} padding={'1'}>
		{ownerInfo}
		{expirationInfo}
	</Box >;
}

type LoginMode = 'User' | 'Code';
type Props = { initialUsers: AvailableUserDTO[]; licenseInfo: LicenseContainerDTO, resellerBanner: ResellerBannerDTO, configuration: GetDeviceConfigurationResponse, loginMode: LoginMode } & Modal.Actions<void>;

declare global {
	interface CommandType {
		login: () => Promise<Result>
	}
}

async function displayClosedCashdrawer() {
	const businessDay = await CashdrawerFacade.getCurrentBusinessDay();
	if (businessDay === null)
		await CustomerDisplay.displayClosedCashdrawer();
}

async function canAccessToConfiguration(): Promise<boolean> {
	const validEmptyPassword = await LoginFacade.validateResellerPassword('');
	if (!validEmptyPassword) {
		const resellerPasswordResult = await getResellerPassword();
		if (!resellerPasswordResult.ok)
			return false;

		const isValidResellerPassword = await LoginFacade.validateResellerPassword(resellerPasswordResult.value);
		if (!isValidResellerPassword) {
			Toaster.error(Res.Msg.MSG_INVALID_RESELLER_PASSWORD());
			return false;
		}
	}

	return true;
}

async function runSessionFlow(doLogin: () => Promise<{ ok: boolean, errorMessage: string }>) {

	await CertificateExpirationNotification.notifyExpirationIfNeeded();

	const { ok, errorMessage } = await doLogin();
	if (!ok) {
		Toaster.error(errorMessage || Res.Msg.MSG_INVALID_USER_PASSWORD());
		return;
	}

	const openDayResult = await openDayIfNeeded();
	if (openDayResult.ok) {

		TakeOutOrdersMonitor.start();

		await Command.execute('selectSaleLocation');

		TakeOutOrdersMonitor.stop();
	}

	await LoginService.logout();

	await displayClosedCashdrawer();
}

Command.register('login', 'NO_PRECONDITION', async (): Promise<Result> => {
	const [users, container, banner, configuration, loginMode] = await Promise.all([
		RestaurantFacade.getAvailableUsers(),
		RestaurantFacade.getLicenseContainerAndVersion(),
		RestaurantFacade.getResellerBanner(),
		RestaurantFacade.getDeviceConfiguration(),
		LocalStorage.getItem(LOGIN_MODE_KEY)
	]);

	await displayClosedCashdrawer();

	return await Modal.show(SelectUserScreen, { configuration, initialUsers: users, resellerBanner: banner, licenseInfo: container, loginMode: loginMode ? loginMode as LoginMode : 'User' });
});

type CardNumberState = { cardNumber: string }

const cardNumberSchema = z.object({
	cardNumber: z.string().nonempty(Res.Msg.MSG_CARD_NUMBER_REQUIRED())
});

const UserListContent = React.memo(function UserListContent({ users, onUserSelect }: { users: AvailableUserDTO[], onUserSelect: (user: AvailableUserDTO) => Promise<void> }): React.JSX.Element {

	return <VStack height={'full'} overflowY={'auto'}>
		<LargeDeviceOnly><VerifactuNotificationBar /></LargeDeviceOnly>

		<Stack width={'full'} gap={'6'} padding={{ base: '6', smDown: '4' }}>
			<Box fontSize={'md'} fontWeight={'semibold'} w='full'>
				{Res.Web.SELECT_USER_LBL()}
			</Box>
			<Box flexGrow={1} w='full'>
				<Grid gap={{ smDown: '2', 'phone-lgToMd': '5', md: '4.5', xl: '5.5' }} templateColumns={{ base: 'repeat(auto-fill, 6rem)', smDown: 'repeat(auto-fill, 4.75rem)' }} overflow={'hidden'} >
					{
						users.map(user => (
							<Box key={user.Id}>
								<UserButton user={user} onSelect={onUserSelect} />
							</Box>
						))
					}
				</Grid>
			</Box>
		</Stack>
	</VStack>;
}, (prev, next) => prev.users === next.users);

function CardNumberListContent({ ref }: { ref: React.RefObject<FormRef<CardNumberState> | null> }): React.JSX.Element {
	const { Form, Input } = useControlsFor<CardNumberState>();

	useEffectOnce(() => {
		ref.current?.setFocus?.('cardNumber');
	});

	return (
		<Box paddingX='6' paddingY='4' height={'full'} alignContent={'center'}>
			<VStack w='full' justifyContent={'center'} gap='1' mb='4'>
				<Images.Profile width='48' height='48' />
			</VStack>
			<Form initialValue={{ cardNumber: '' }} schema={cardNumberSchema} ref={ref}>
				<VStack w='full' justifyContent={'center'} gap='6'>
					<Text fontSize={'md'} fontWeight={'bold'}>{'¡' + Res.Web.WELCOME_TO_AGORA_LBL() + '!'}</Text>
					<Input linkTo={'cardNumber'} placeholder={Res.Web.SELECT_USER_USER_CARD_NUMBER_PLACEHOLDER()} />
				</VStack>
			</Form>
		</Box>
	);
}

function SelectUserScreen(props: Props) {

	const screenRef = React.useRef<HTMLInputElement>(null);
	const handlingLogin = React.useRef(false);

	const [users, setUsers] = React.useState<AvailableUserDTO[]>(props.initialUsers);
	const [mode, setMode] = React.useState<LoginMode>(props.loginMode);
	const [fingerprintStatus, setFingerprintStatus] = React.useState<ReaderStatus>({ failure: '', message: '', verifiedUserId: null });

	const cardNumberFormRef = React.useRef<FormRef<CardNumberState>>(null);

	const lastSale = SalesStore.getLastSale();

	const { execute } = useOverlayExecutor();

	const reloadUsers = async () => {
		setUsers(await RestaurantFacade.getAvailableUsers());
	};

	const handleLogin = async (doLogin: () => Promise<{ ok: boolean, errorMessage: string }>) => {

		if (handlingLogin.current)
			return;

		handlingLogin.current = true;
		await execute({
			action: async () => {

				barcodeReader.current?.stop();


				await runSessionFlow(doLogin);
				await reloadUsers();

				barcodeReader.current?.start();
			}
		});
		handlingLogin.current = false;
	};

	const onCardNumberRead = async (cardNumber: string) => {
		await handleLogin(() => LoginService.loginByCardNumber(cardNumber));
	};

	const barcodeReader = useBarcodeReaderRef(onCardNumberRead);

	const onFingerprintRead = React.useCallback(async (status: ReaderStatus) => {

		if (status.failure)
			Toaster.error(status.failure);

		if (status.message)
			setFingerprintStatus(status);

		if (status.verifiedUserId !== null)
			await handleLogin(() => LoginService.loginByUserId(status.verifiedUserId!));

		// No quiero crear varias referencias de onFingerprintRead para useFingerprintReaderRef
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useFingerprintReaderRef(onFingerprintRead);

	const onUserSelect = async (user: AvailableUserDTO) => {

		const passwordResult = await getPassword(user);
		if (!passwordResult.ok)
			return;

		await handleLogin(() => LoginService.login(user.Name, passwordResult.value));
	};

	const onConfigureSettings = async () => {

		if (!await canAccessToConfiguration())
			return;

		const result = await configureSettings();
		if (result.ok)
			setTimeout(() => window.location.reload());
	};

	const onDoClocking = async () => {
		await Command.execute('doClocking');
	};

	const closeApp = async () => {
		const result = await Message.confirm(Res.Msg.MSG_CONFIRM_EXIT_APP());
		if (result.ok)
			await Command.execute('closeApp');
	};

	const selectUserByCard = async () => {
		const result = await cardNumberFormRef.current!.getValue();
		if (result.ok)
			await onCardNumberRead(result.value.cardNumber);
	};

	const switchAccessMode = async () => {
		const newMode = mode === 'Code' ? 'User' : 'Code';
		LocalStorage.setItem(LOGIN_MODE_KEY, newMode);
		setMode(newMode);
	};


	const showClockingButton = ThemeManager.isWebPos() && SessionProvider.isAttendanceModuleEnabled();

	return (
		<PosScreen screenId='SELECT_USER' ref={screenRef} size={'full-screen'} useKeyPad={mode === 'Code'} showOnlyWhileOnTop>

			<Header title={mode === 'User' ? Res.Web.SELECT_USER() : Res.Web.SELECT_USER_IDENTIFICATION()} image={Images.AgoraLogo} onClose={() => { }} />

			<Sidebar>
				{showClockingButton && <SidebarButton text={Res.Web.SELECT_USER_ATTENDANCE_BTN()} icon={Images.Clocking} action={onDoClocking} />}
				{Electron.isEnabled() ? null : <SidebarButton text={Res.Web.SELECT_USER_SETTINGS_BTN()} icon={Images.Config} action={onConfigureSettings} />}
				{Electron.isEnabled() ? <SidebarButton text={Res.Web.SELECT_USER_EXIT_BTN()} icon={Images.Exit} action={closeApp} /> : null}
			</Sidebar>

			<Content>
				<LargeDeviceOnly>{SessionProvider.showLastSaleInfoInLoginView() && <LastSaleBanner lastSale={lastSale} />}</LargeDeviceOnly>
				{
					mode === 'User'
						? <UserListContent users={users} onUserSelect={onUserSelect} />
						: <CardNumberListContent ref={cardNumberFormRef} />
				}

			</Content>

			<Footer>
				<LargeDeviceOnly>
					<Filler><ResellerBanner {...props.resellerBanner} /></Filler>
					<LicenseInfo {...props.licenseInfo} />
					<Filler display='flex' justifyContent={'end'}><FingerprintReaderStatus enabled={props.configuration.UseFingerprintReader} message={fingerprintStatus.message} /></Filler>
				</LargeDeviceOnly>
				<PhoneOnly>
					{
						mode === 'User'
							? <>
								<ActionButton image={Images.Config} title={Res.Web.SELECT_USER_SETTINGS_BTN()} action={onConfigureSettings} />
								<Filler display='flex' justifyContent={'center'}><LicenseInfo {...props.licenseInfo} /></Filler>
								<ActionButton title={Res.Web.SELECT_USER_CARD_NUMBER_BTN()} image={Images.UserCard} action={switchAccessMode} />
							</>

							: <>
								<ActionButton image={Images.User} action={switchAccessMode} />
								<Filler display='flex' justifyContent={'center'}><LicenseInfo {...props.licenseInfo} /></Filler>
								<AcceptActionButton action={selectUserByCard} />
							</>
					}
				</PhoneOnly>
			</Footer>

		</PosScreen>
	);
}
